import api from "./api";

export interface SunbedItem {
  id: number | string;
  pricing: Array<{
    id: number | string;
    type: "fixed" | "per_minute";
    value: string;
    price: number | string;
  }>;
  selectedPricingId: number | string;
  selectedMinutes?: number; // Required for per_minute type
  company_id?: number;
}

export interface PosStorePayload {
  // Services (from appointments/bookings)
  cart_services?: (string | number)[];
  cart_quantity?: number[];
  cart_prices?: number[];
  tax_percent?: number[]; // Tax percent per service (for BookingItem)
  // Products (from POS)
  product_cart_services?: (string | number)[];
  product_cart_quantity?: number[];
  product_cart_prices?: number[];
  product_tax_percent?: number[]; // Tax percent per product
  // Deals (package of services + products)
  cart_deals?: (string | number)[];
  deal_cart_quantity?: number[];
  deal_cart_prices?: number[];
  // Sunbeds (from appointments/bookings)
  calendar_sunbeds?: SunbedItem[];
  calendar_memberships?: Array<{ plan_id: number; price?: number }>;
  location: number | string;
  pos_date: string; // Format: Y-m-d (e.g., "2025-12-05")
  pos_time: string; // Format: H:i a (e.g., "10:02 pm")
  date?: string; // Format: d-m-Y (e.g., "05-12-2025") - alternative format
  time?: string; // Format: H:i a (e.g., "10:02 pm") - alternative format
  posTime?: string; // Format: H:i a (e.g., "10:02 pm") - alternative format
  user_id: number | string;
  payments: Array<{
    type: "cash" | "cheque" | "card";
    amount: number;
  }>;
  payment_gateway?: string; // "Multiple" or single payment method
  pending_amount: number | string;
  amount_paid: number;
  change_due?: number | string;
  type?: "pay_later" | "part_paid" | string;
  serviceType?: "online" | "offline";
  total_tax?: number;
  cart_discount?: number;
  coupon_id?: number | string | null | "";
  coupon_amount?: number | string | null | "";
  coupon?: string | null | "";
  uuid?: string | null;
  transaction_id?: string | null;
  employee?: (number | string | null)[];
  consultation_form_ids?: number[];
  /** One value per booking item (services then products), 1 = in-store usage, 0 = normal. Backend uses per-item. */
  in_store_usage?: number[];
}

export interface Currency {
  id: number;
  currency_name: string;
  currency_symbol: string;
  currency_code: string;
  exchange_rate: number;
  usd_price?: number | null;
  is_cryptocurrency: string;
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Company {
  id: number;
  currency_id: number;
  package_id: number;
  company_name: string;
  slug: string;
  template: string;
  subdomain: string;
  website_status: string;
  is_claim: string;
  custom_domain_status?: string | null;
  custom_domain?: string | null;
  company_email: string;
  company_phone: string;
  company_phone_2?: string | null;
  contact_email?: string | null;
  logo?: string | null;
  address: string;
  locality: string;
  date_format: string;
  time_format: string;
  website: string;
  timezone: string;
  receipt_format: string;
  locale: string;
  latitude?: number | null;
  longitude?: number | null;
  trial_ends_at?: string | null;
  licence_expire_on?: string | null;
  disable_slot: string;
  booking_time_type: string;
  booking_per_day?: number | null;
  employee_selection: string;
  multi_task_user?: string | null;
  popular_store: string;
  created_at: string;
  updated_at: string;
  status: string;
  verified: string;
  stripe_id?: string | null;
  card_brand?: string | null;
  card_last_four?: string | null;
  package_type?: string | null;
  cron_status: string;
  display_deal: string;
  approve_online_booking: string;
  approve_offline_booking: string;
  duration: number;
  duration_type: string;
  pay_with_xeconnect: string;
  vkb_is_visible: number | boolean;
  vkb_position: string;
  landline?: string | null;
  show_contact_details: number | boolean;
  quick_payment_type: string;
  is_sunbed: number;
  is_sunbed_tmax: number;
  logo_url?: string;
  formatted_phone_number?: string;
  formatted_address?: string;
  formatted_website?: string;
  company_verification_url?: string;
  google_account?: string | null;
  currency?: Currency;
  [key: string]: any;
}

export interface BookingItem {
  id: number;
  company_id: number;
  employee_id?: number | null;
  deal_id?: number | null;
  booking_id: number;
  business_service_id?: number | null;
  product_id: number;
  sunbed_id?: number | null;
  sunbed_pricing_id?: number | null;
  sunbed_mins?: number | null;
  quantity: number;
  unit_price: number;
  amount: number;
  tax_percent?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface Location {
  id: number;
  name: string;
  lat: number;
  lng: number;
  country_id: number;
  timezone_id: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface Booking {
  id: number;
  user_id: string;
  date_time: string;
  location_id: string;
  status: string;
  payment_gateway: string;
  original_amount: number;
  booking_type: string;
  product_amount: number;
  discount: number;
  discount_percent: string;
  uuid?: string | null;
  transaction_id?: string | null;
  payment_status: string;
  tax_name: string;
  tax_percent: number;
  tax_amount: number;
  amount_to_pay: number;
  company_id: number;
  event_id?: number | null;
  updated_at: string;
  created_at: string;
  formated_amount_to_pay: string;
  company?: Company;
  items?: BookingItem[];
  location?: Location;
  [key: string]: any;
}

export interface PosStoreResponse {
  payment_status: "completed" | "pending";
  booking: Booking;
  message: string;
  booking_id?: number | string; // Keep for backward compatibility, but prefer booking.id
}

export const storePosBooking = async (
  payload: PosStorePayload
): Promise<PosStoreResponse> => {
  // Format payments as string like old system: "15.00 (Paid by Cash),171.79 (Paid by Cheque)"
  const paymentString = payload.payments
    .map((p) => {
      const methodName = p.type.charAt(0).toUpperCase() + p.type.slice(1);
      return `${p.amount.toFixed(2)} (Paid by ${methodName})`;
    })
    .join(",");

  // Determine payment gateway
  const paymentGateway =
    payload.payments.length > 1
      ? "Multiple"
      : payload.payments[0]?.type.charAt(0).toUpperCase() +
      payload.payments[0]?.type.slice(1) || "Cash";

  // Format date in d-m-Y format (alternative format from old system)
  const formatDateDMY = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Build FormData to properly handle array notation for Laravel
  const formData = new FormData();

  // Add service arrays with bracket notation (Laravel array format)
  if (payload.cart_services && payload.cart_services.length > 0) {
    payload.cart_services.forEach((id) => {
      formData.append('cart_services[]', String(id));
    });
    (payload.cart_quantity || []).forEach((qty) => {
      formData.append('cart_quantity[]', String(qty));
    });
    (payload.cart_prices || []).forEach((price) => {
      formData.append('cart_prices[]', String(price));
    });
    (payload.tax_percent || []).forEach((tax) => {
      formData.append('tax_percent[]', String(tax));
    });
  }

  // Add product arrays with bracket notation (Laravel array format)
  if (payload.product_cart_services && payload.product_cart_services.length > 0) {
    payload.product_cart_services.forEach((id) => {
      formData.append('product_cart_services[]', String(id));
    });
    (payload.product_cart_quantity || []).forEach((qty) => {
      formData.append('product_cart_quantity[]', String(qty));
    });
    (payload.product_cart_prices || []).forEach((price) => {
      formData.append('product_cart_prices[]', String(price));
    });
    (payload.product_tax_percent || []).forEach((tax) => {
      formData.append('product_tax_percent[]', String(tax));
    });
  }

  if (payload.cart_deals && payload.cart_deals.length > 0) {
    payload.cart_deals.forEach((id) => {
      formData.append("cart_deals[]", String(id));
    });
    (payload.deal_cart_quantity || []).forEach((qty) => {
      formData.append("deal_cart_quantity[]", String(qty));
    });
    (payload.deal_cart_prices || []).forEach((price) => {
      formData.append("deal_cart_prices[]", String(price));
    });
  }

  // Add sunbeds as JSON string (backend expects JSON format)
  if (payload.calendar_sunbeds && payload.calendar_sunbeds.length > 0) {
    const sunbedsJson = JSON.stringify(payload.calendar_sunbeds);
    console.log("Sending calendar_sunbeds to backend:", sunbedsJson);
    formData.append('calendar_sunbeds', sunbedsJson);
  }

  if (payload.calendar_memberships && payload.calendar_memberships.length > 0) {
    formData.append('calendar_memberships', JSON.stringify(payload.calendar_memberships));
  }

  // Add employee array if provided
  if (payload.employee && payload.employee.length > 0) {
    payload.employee.forEach((emp) => {
      if (emp !== null) {
        formData.append('employee[]', String(emp));
      }
    });
  }

  // Add other fields
  formData.append('location', String(payload.location));
  formData.append('pos_date', payload.pos_date);
  formData.append('pos_time', payload.pos_time);
  if (payload.date) formData.append('date', payload.date);
  if (payload.time) formData.append('time', payload.time);
  if (payload.posTime) formData.append('posTime', payload.posTime);
  if ((payload as any).booking_id) formData.append('booking_id', String((payload as any).booking_id));
  formData.append('user_id', String(payload.user_id));
  formData.append('payments', paymentString);
  formData.append('payment_gateway', payload.payment_gateway || paymentGateway);
  formData.append('pending_amount', String(payload.pending_amount));
  formData.append('amount_paid', String(payload.amount_paid));
  formData.append('change_due', String(payload.change_due || 0));
  formData.append('serviceType', payload.serviceType || 'offline');
  formData.append('total_tax', String(payload.total_tax || 0));
  formData.append('cart_discount', String(payload.cart_discount || 0));
  formData.append('coupon_id', String(payload.coupon_id || ''));
  formData.append('coupon_amount', String(payload.coupon_amount || ''));
  formData.append('coupon', String(payload.coupon || ''));
  formData.append('uuid', String(payload.uuid || ''));
  formData.append('transaction_id', String(payload.transaction_id || ''));

  // Add type if provided (for pay_later)
  if (payload.type) {
    formData.append('type', payload.type);
  }

  // ================================
  // ADD CONSULTATION FORM IDS
  // ================================
  if (payload.consultation_form_ids !== undefined) {
    formData.append(
      "consultation_form_ids",
      JSON.stringify(payload.consultation_form_ids),
    );
  }

  // ================================
  // ADD IN_STORE_USAGE (per item: 1 = store usage, 0 = normal)
  // ================================
  if (payload.in_store_usage && payload.in_store_usage.length > 0) {
    payload.in_store_usage.forEach((val) => {
      formData.append("in_store_usage[]", String(val));
    });
  }

  const response = await api.post<PosStoreResponse>("/admin/pos", formData);
  return response.data;
};

export interface ApplyPosCouponPayload {
  coupon: string;
  /** Cart total (incl. tax) — preferred for POS / Quick Sale mixed carts */
  cart_amount?: number;
  /** Legacy: [[serviceId, qty], ...] */
  cart_services?: Array<[number | string, number]>;
  cart_discount?: number;
}

export interface ApplyPosCouponResponse {
  message: string;
  amount: number;
  coupon_data: {
    id: number;
    code: string;
    title?: string;
    amount: number | string;
    discount_type: "percentage" | "amount" | string;
    [key: string]: unknown;
  };
}

export async function applyPosCoupon(
  payload: ApplyPosCouponPayload,
): Promise<ApplyPosCouponResponse> {
  const response = await api.post<ApplyPosCouponResponse>(
    "/admin/pos/apply-coupon",
    payload,
  );
  return response.data;
}
